// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import sendRmaRequest from "@insite/client-framework/Store/Pages/RequestRma/Handlers/SendRmaRequest";
import setReturnNotes from "@insite/client-framework/Store/Pages/RequestRma/Handlers/SetReturnNotes";
import translate from "@insite/client-framework/Translate";
import { TwoButtonModalStyles } from "@insite/content-library/Components/TwoButtonModal";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import { ClickableProps } from "@insite/mobius/Clickable";
import { HiddenProps } from "@insite/mobius/Hidden";
import { OverflowMenuProps } from "@insite/mobius/OverflowMenu/OverflowMenu";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@werner/App/Components/Notify/Notify";
import RequestRmaContactForm from "@werner/Overrides/Widgets/RequestRma/RequestRma.ContactForm";
import { ContactFormState, initialContactFormState } from "@werner/Overrides/Widgets/RequestRma/RequestRma.form.module";
import { mergeWithCss, modalButtonsActionStyles, xScreen, xValidateOnSubmit } from "@werner/XDL/Utilities";
import { CreateForm } from "@werner/XDL/Utilities/form";
import cloneDeep from "lodash/cloneDeep";
import React, { FC, useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// XNG-Changes
const formId = "requestRMAZipCode";

interface OwnProps {
    extendedStyles?: RequestRmaButtonsStyles;
}

const mapStateToProps = (state: ApplicationState) => ({
    orderDetailsLink: getPageLinkByPageType(state, "OrderDetailsPage"),
    resultMessage: state.pages.requestRma.resultMessage,
    canSendReturnRequest: state.pages.requestRma.canSendReturnRequest,
});

const mapDispatchToProps = {
    sendRmaRequest,
    setReturnNotes,
};

export interface RequestRmaButtonsStyles {
    buttonsHiddenContainer?: HiddenProps;
    menuHiddenContainer?: HiddenProps;
    narrowOverflowMenu?: OverflowMenuProps;
    sendButton?: ButtonPresentationProps;
    cancelButton?: ButtonPresentationProps;
    sendClickable?: ClickableProps;
    cancelClickable?: ClickableProps;
    sendReturnRequestModal?: TwoButtonModalStyles;
    // XNG-Changes:
    wrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    checkboxWrapper?: InjectableCss;
    pickUpCheckbox?: CheckboxPresentationProps;
    contactFormWrapper?: InjectableCss;
    actionsWrapper?: InjectableCss;
    buttonsWrapper?: InjectableCss;
    termsText?: TypographyPresentationProps;
}

export const requestRmaButtonsStyles: RequestRmaButtonsStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
        `,
    },
    titleWrapper: {
        css: css`
            display: flex;
            width: 100%;
            margin-top: 32px;
        `,
    },
    title: {
        variant: "h4",
        css: css`
            margin-bottom: 32px;
        `,
    },
    checkboxWrapper: {
        css: css`
            margin-bottom: 24px;
        `,
    },
    contactFormWrapper: {
        css: css`
            display: flex;
            width: 100%;
            margin-bottom: 32px;
        `,
    },
    actionsWrapper: {
        css: css`
            display: flex;
            width: 100%;
            justify-content: space-between;
            margin-top: 12px;
            ${xScreen("lte", "sm")} {
                flex-direction: column;
                gap: 20px;
            }
        `,
    },
    buttonsWrapper: {
        css: css`
            display: flex;
            ${xScreen("lte", "sm")} {
                justify-content: flex-end;
            }
        `,
    },
    cancelButton: {
        ...mergeWithCss(modalButtonsActionStyles.cancelButton, {
            css: css`
                height: 46px;
            `,
        }),
    },
    sendButton: {
        ...mergeWithCss(modalButtonsActionStyles.submitButton, {
            css: css`
                height: 46px;
            `,
        }),
    },
};

const styles = requestRmaButtonsStyles;

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    HasToasterContext;

const RequestRmaButtons: FC<Props> = ({
    orderDetailsLink,
    resultMessage,
    canSendReturnRequest,
    history,
    toaster,
    sendRmaRequest,
    setReturnNotes,
}) => {
    const [modalIsOpen, setModalIsOpen] = React.useState(false);
    const { value: order } = useContext(OrderStateContext);
    // const [pickupRequired, setPickupRequired] = React.useState(false);
    // const [contactFormData, setContactFormData] = useState<ContactFormState>({ isLoading: false });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const [contactState, setContactState] = useState<ContactFormState>(cloneDeep(initialContactFormState));
    const contactForm = new CreateForm<ContactFormState>(contactState, setContactState);

    if (!order) {
        return null;
    }

    const cancelClickHandlel = () => {
        if (!orderDetailsLink) {
            return;
        }

        setReturnNotes({ returnNotes: "" });
        history.push(`${orderDetailsLink.url}?orderNumber=${order.webOrderNumber}`);
    };

    const modalOpenHandler = () => {
        setModalIsOpen(true);
    };

    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };

    const sendReturnRequestHandler = () => {
        contactForm.setSubmitted();
        // XNG-Changes
        // if (pickupRequired) {
        contactForm.validate();
        // }

        // if (pickupRequired && !contactForm.state.isValid) {
        //     return;
        // }

        if (!contactForm.state.isValid) {
            return;
        }

        const { fields } = contactForm.state;
        const properties: { [key: string]: string } = {};

        const errors = xValidateOnSubmit(formId);
        if (errors.length > 0) {
            return;
        }

        Object.keys(fields).map((field, index) => {
            if (!properties[field]) {
                // XNG-Changes
                if (field === "pickupDate") {
                    properties[field] = new Date(fields[field as keyof typeof fields].value).toLocaleDateString();
                } else {
                    properties[field] = fields[field as keyof typeof fields].value;
                }
            }
            return true;
        });

        sendRmaRequest({
            orderNumber: order.webOrderNumber || order.erpOrderNumber,
            // XNG-Changes
            // properties: { ...properties, pickupRequired: pickupRequired ? "true" : "false" },
            properties: { ...properties, pickupRequired: "true" },
            onSuccess: () => {
                // setModalIsOpen(false);
                toaster.addToast({
                    body: siteMessage(resultMessage || translate("Request submitted.")),
                    messageType: "success",
                });
                // XNG-Changes : WES-1913 : Pass country to zipCode validatorsOptions for zip code validation
                setContactState(cloneDeep(initialContactFormState));
                setReturnNotes({ returnNotes: "" });
                // setPickupRequired(false);
            },
            onError: (errorMessage: string) => {
                toaster.addToast({ body: errorMessage, messageType: "danger" });
            },
            onComplete(resultProps: any) {
                if (resultProps.apiResult?.successful) {
                    // "this" is targeting the object being created, not the parent SFC
                    this.onSuccess?.(resultProps.apiResult.result);
                } else if (resultProps.apiResult?.errorMessage) {
                    // "this" is targeting the object being created, not the parent SFC
                    this.onError?.(resultProps.apiResult.errorMessage);
                }
            },
        } as any);
    };
    // XNG-Changes: Change Structure
    return (
        <>
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.titleWrapper}>
                    <Typography {...styles.title}>{translate("PickUp Information")}</Typography>
                </StyledWrapper>
                {/* XNG-Changes */}
                {/* <StyledWrapper {...styles.checkboxWrapper}>
                    <CheckboxGroup>
                        <Checkbox
                            {...styles.pickUpCheckbox}
                            checked={pickupRequired}
                            onChange={() => {
                                setPickupRequired(!pickupRequired);
                            }}
                        >
                            {translate("Pickup Required")}
                            <VisuallyHidden>{translate("Pickup Required")}</VisuallyHidden>
                        </Checkbox>
                    </CheckboxGroup>
                </StyledWrapper> */}
                {/* {pickupRequired && ( */}
                <StyledWrapper {...styles.contactFormWrapper}>
                    <RequestRmaContactForm contactForm={contactForm} />
                </StyledWrapper>
                {/* )} */}
                {/* {createWidgetElement("RequestRma/RequestRmaNotes", { fields: {} })} */}
                <StyledWrapper {...styles.actionsWrapper}>
                    <Notify type="info" data-test-selector="RequestRma_terms_of_service_message" showClose={false}>
                        {siteMessage("Rma_Terms_Of_Service")}
                    </Notify>
                    <StyledWrapper {...styles.buttonsWrapper}>
                        <Button
                            {...styles.cancelButton}
                            onClick={cancelClickHandlel}
                            data-test-selector="requestRmaHeader_cancel"
                            aria-label="Cancel"
                        >
                            {translate("Cancel")}
                        </Button>
                        <Button
                            {...styles.sendButton}
                            onClick={sendReturnRequestHandler}
                            disabled={!canSendReturnRequest}
                            data-test-selector="requestRmaHeader_send"
                            aria-label="Send Return Request"
                        >
                            {translate("Send Return Request")}
                        </Button>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(withToaster(RequestRmaButtons)));
